<template>
    <div>
      <add-or-update-church-map-modal
        :is-add-or-update-active.sync="isAddOrUpdateActive"
        :update-data="updateData"
        @refetchData="fetchAllData"
        @resetUpdateData="resetUpdateData"
      />
      
      <list-table
        :records="churchMap"
        :columns="tableColumns"
        :total-records="totalRecords"
        :search-filter.sync="search"
        :current-page-number.sync="currentPage"
        :show-search="true"
        :show-export-button="true"
        :show-filter-by-default="true"
        :show-import-button="can('church_map:CanImport')"
        import-type="church_map"
        @export="onExportChurchMap"
        @import="onImport"
      >
        <template #filters>
          <b-row>
            <b-col cols="12" md="3" class="">
              <v-select
                v-model="regionFilter"
                placeholder="Select Region"
                :loading="loading"
                :clearable="true"
                :options="regionOptions"
                :reduce="(val) => val.value"
                :searchable="true"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              />
            </b-col>
            <b-col cols="12" md="3" class="">
              <v-select
                v-model="areaFilter"
                placeholder="Select Area"
                :loading="loading"
                :clearable="true"
                :options="areaOptions"
                :reduce="(val) => val.value"
                :searchable="true"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              />
            </b-col>
            <b-col cols="12" md="3" class="">
              <v-select
                v-model="districtFilter"
                placeholder="Select District"
                :loading="loading"
                :clearable="true"
                :options="districtOptions"
                :reduce="(val) => val.value"
                :searchable="true"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              />
            </b-col>
            <b-col cols="12" md="3" class="">
              <v-select
                v-model="assemblyTypeFilter"
                placeholder="Select Assembly Type"
                :loading="loading"
                :clearable="true"
                :options="assemblyTypeOptions"
                :reduce="(val) => val.value"
                :searchable="true"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              />
            </b-col>
          </b-row>
        </template>

        <template v-if="can('church_map:CanAddNew')" #action_button>
          <b-button
            v-ripple.400="'rgba(113, 102, 240, 0.15)'"
            variant="primary"
            @click="isAddOrUpdateActive = true"
          >
            <span class="align-middle text-lg">Add New Church Map</span>
          </b-button>
        </template>

        <template #cell(actions)="data">
          <b-dropdown
            variant="link"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
  
            <b-dropdown-item v-if="can('church_map:CanUpdate')" @click="onEdit(data.item)">
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50">Edit</span>
            </b-dropdown-item>
  
             <b-dropdown-item v-if="can('church_map:CanDelete')" @click="onDelete(data.item._id)">
              <feather-icon icon="DeleteIcon" />
              <span class="align-middle ml-50">Delete</span>
             </b-dropdown-item>
          </b-dropdown>
        </template>
      </list-table>
    </div>
  </template>
  
  <script>
  import vSelect from 'vue-select'
  import FileDownload from "js-file-download";
  import Ripple from 'vue-ripple-directive'
  import ListTable from "@core/components/ListTable/ListTable.vue";
  import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
  import AddOrUpdateChurchMapModal from "@core/components/shared/church/AddOrUpdateChurchMapModal.vue";

  import { debounce, get } from "lodash";
  import { formatDate } from "@core/utils/utils";
  import { BLink, BButton, BModal, BRow, BCol, BTooltip, BAvatar, BMedia, BBadge, BDropdown, BDropdownItem } from "bootstrap-vue";
  
  const watchHandler = {
    handler(){
      this.debounceFetchAllData()
    },
    immediate: false
  }
  
  const watchHandlerRequiresPageReset = {
    handler(){
      this.currentPage = 1;
      this.debounceFetchAllData()
    },
    immediate: false
  }
  
  export default {
    name: "ChurchMap",
    components: {
      BRow,
      BCol,
      BLink,
      BModal,
      BMedia,
      BBadge,
      BButton,
      vSelect,
      BAvatar,
      BTooltip,
      ListTable,
      BDropdown,
      BDropdownItem,
      AddOrUpdateChurchMapModal
    },
    directives: {
      Ripple,
    },
    data() {
      return {
        loading: false,
        churchMap: [],

        search: "",
        updateData: null,
        debounceFetchAllData: null,
        isAddOrUpdateActive: false,

        restoringFilters: false,
        regionFilter: null,
        areaFilter: null,
        districtFilter: null,
        assemblyTypeFilter: null,
        regionOptions: [],
        areaOptions: [],
        districtOptions: [],
        assemblyTypeOptions: [],

        tableColumns: [
          { key: 'region.title', label: "Region", sortable: false },
          { key: 'area.title', label: "Area", sortable: false },
          { key: 'assembly.title', label: "Assembly", sortable: false },
          { key: 'district.title', label: "District", sortable: false },
          { key: 'assembly_type', sortable: false },
          { key: 'latitude', sortable: false },
          { key: 'longitude', sortable: false },
          { key: 'created', sortable: false, formatter: val => `${formatDate(val, 'dd-MM-yy h:m:s a')}`, label: 'Date', class: 'width-200' },
          { key: 'actions' },
        ]
      }
    },
    computed: {
      perPage: {
        get() {
          return this.$store.getters[`navigation/recordsPerPage`];
        },
        set(value){
          this.$store.commit(`navigation/UPDATE_RECORDS_PER_PAGE`, value)
        }
      },
      currentPage: {
        get() {
          return this.$store.getters[`navigation/currentPage`];
        },
        set(value){
          this.$store.commit(`navigation/UPDATE_CURRENT_PAGE_NUMBER`, value)
        }
      },
      totalRecords: {
        get(){
          return this.$store.getters[`navigation/totalRecords`];
        },
        set(value){
          this.$store.commit(`navigation/UPDATE_TOTAL_RECORDS`, value)
        }
      },
    },
    watch: {
      currentPage: watchHandler,
      search: watchHandlerRequiresPageReset,
      perPage: watchHandlerRequiresPageReset,
      districtFilter: watchHandlerRequiresPageReset,
      assemblyTypeFilter: watchHandlerRequiresPageReset,
      regionFilter: {
        handler(regionId) {
          if (!this.restoringFilters) {
            this.areaFilter = null;
            this.districtFilter = null;
            this.areaOptions = [];
            this.districtOptions = [];
            if (regionId) {
              this.fetchAreaOptions(regionId);
            }
          }
          this.currentPage = 1;
          this.debounceFetchAllData();
        },
        immediate: false
      },
      areaFilter: {
        handler(areaId) {
          if (!this.restoringFilters) {
            this.districtFilter = null;
            this.districtOptions = [];
            if (areaId) {
              this.fetchDistrictOptions(areaId);
            }
          }
          this.currentPage = 1;
          this.debounceFetchAllData();
        },
        immediate: false
      },
    },
    created() {
      this.debounceFetchAllData = debounce(this.fetchAllData, 500);

      const {
        search,
        page = 1,
        limit = this.perPage,
        region_id,
        area_id,
        district_id,
        assembly_type,
      } = this.$route.query;

      this.fetchRegionOptions();
      this.fetchAssemblyTypeOptions();

      this.restoringFilters = true;
      if (region_id) {
        this.regionFilter = region_id;
        this.fetchAreaOptions(region_id);
      }
      if (area_id) {
        this.areaFilter = area_id;
        this.fetchDistrictOptions(area_id);
      }
      if (district_id) this.districtFilter = district_id;
      if (assembly_type) this.assemblyTypeFilter = assembly_type;
      this.restoringFilters = false;

      this.search = search;
      this.perPage = +limit;
      this.currentPage = +page;
    },
    methods: {
      async fetchAllData() {
        try {
          this.loading = true;

          const query = {
            limit: this.perPage,
            search: this.search,
            page: this.currentPage,
            ...this.buildFilterQuery(),
          }
          await this.$router.push({ query }).catch(() => {});

          const request = await this.useJwt().mainService.churchMap.list(query);
          const { data, pagination } = request.data;
  
          this.totalRecords = pagination.totalRecords;
          this.perPage = pagination.limit;
          this.churchMap = data;
        } catch (error) {
          const error_message = get(error, "response.data.message") || error.message
          console.log("error_message", error_message);
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error!',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              text: error_message
            },
          });
        } finally {
          this.loading = false;
        }
      },
      async onDelete(id){
        try {
          this.loading = true;
  
          const result = await this.$swal({
            title: 'Confirm Delete?',
            text: "Do you want to delete this record?",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, proceed.',
            customClass: {
              confirmButton: 'btn btn-primary',
              cancelButton: 'btn btn-outline-danger ml-1',
            },
            buttonsStyling: false,
          });
  
          if (!result.value) {
            return;
          }
  
          await this.useJwt().mainService.churchMap.delete(id);
          await this.fetchAllData()
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Success',
              icon: 'CheckCircleIcon',
              variant: 'success',
              text: 'Record deleted Successfully'
            },
          });
        } catch (error) {
          const error_message = get(error, "response.statusText") || error.message;
  
          this.$nextTick(() => {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Error!',
                icon: 'AlertTriangleIcon',
                variant: 'danger',
                text: error_message
              },
            });
          })
        } finally {
          this.loading = false;
        }
      },
      onEdit(update){
        this.updateData = update;
        this.isAddOrUpdateActive = true;
      },
      resetUpdateData(){
        if (this.updateData){
          this.updateData = null
        }
      },
      buildFilterQuery() {
        const query = {};
        if (this.regionFilter) query.region_id = this.regionFilter;
        if (this.areaFilter) query.area_id = this.areaFilter;
        if (this.districtFilter) query.district_id = this.districtFilter;
        if (this.assemblyTypeFilter) query.assembly_type = this.assemblyTypeFilter;
        return query;
      },
      async fetchRegionOptions() {
        try {
          const request = await this.useJwt().sharedService.fetchGeoRegions();
          const { data } = request.data;
          this.regionOptions = data.map((opt) => ({ label: opt.title, value: opt._id }));
        } catch (error) {
          const error_message = get(error, "response.data.message") || error.message;
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error!',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              text: error_message
            },
          });
        }
      },
      async fetchAreaOptions(regionId) {
        try {
          const request = await this.useJwt().sharedService.fetchGeoAreas({ region_id: regionId });
          const { data } = request.data;
          this.areaOptions = data.map((opt) => ({ label: opt.title, value: opt._id }));
        } catch (error) {
          const error_message = get(error, "response.data.message") || error.message;
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error!',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              text: error_message
            },
          });
        }
      },
      async fetchDistrictOptions(areaId) {
        try {
          const request = await this.useJwt().sharedService.fetchGeoDistricts({ area_id: areaId });
          const { data } = request.data;
          this.districtOptions = data.map((opt) => ({ label: opt.title, value: opt._id }));
        } catch (error) {
          const error_message = get(error, "response.data.message") || error.message;
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error!',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              text: error_message
            },
          });
        }
      },
      async fetchAssemblyTypeOptions() {
        try {
          const request = await this.useJwt().mainService.churchMap.fetchAssemblyTypes();
          const { data } = request.data;
          this.assemblyTypeOptions = (data || []).map((opt) => ({ label: opt, value: opt }));
        } catch (error) {
          const error_message = get(error, "response.data.message") || error.message;
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error!',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              text: error_message
            },
          });
        }
      },
      async onExportChurchMap() {
        try {
          this.loading = true;

          const result = await this.$swal({
            title: 'Confirm Export?',
            text: "This will export data based on current search and filters.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, proceed.',
            customClass: {
              confirmButton: 'btn btn-primary',
              cancelButton: 'btn btn-outline-danger ml-1',
            },
            buttonsStyling: false,
          });

          if (!result.value) {
            return;
          }

          const query = {
            search: this.search,
            ...this.buildFilterQuery(),
          }

          const response = await this.useJwt().mainService.churchMap.export(query);
          FileDownload(response.data, "church-map-export.csv");
        } catch (error) {
          const error_message = get(error, "response.statusText") || get(error, "response.data.message") || error.message;

          this.$nextTick(() => {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Error!',
                icon: 'AlertTriangleIcon',
                variant: 'danger',
                text: error_message
              },
            });
          })
        } finally {
          this.loading = false;
        }
      },
      async onImport(file) {
        try {
          this.loading = true;

          const formData = new FormData();
          formData.append("church_map_csv", file);

          const response = await this.useJwt().mainService.churchMap.import(formData);
          const { successfulInserts, failedInserts, writeErrors } = response.data.data;

          if (failedInserts) {
            console.log("Church map import errors", writeErrors);
          }

          this.$toast({
            component: ToastificationContent,
            props: {
              title: failedInserts ? 'Import completed with errors' : 'Success!',
              icon: failedInserts ? 'AlertTriangleIcon' : 'CheckCircleIcon',
              variant: failedInserts ? 'warning' : 'success',
              text: `Imported ${successfulInserts} record(s) successfully. ${failedInserts} row(s) failed.`
            },
          });

          await this.fetchAllData();
        } catch (error) {
          const error_message = get(error, "response.data.message") || error.message;

          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error!',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              text: error_message
            },
          });
        } finally {
          this.loading = false;
        }
      },
    }
  }
  </script>
  
  <style scoped>
  
  </style>
  
